<template>
  <div>
    <q-banner
      v-if="conexaoCaixaSemAcessoRelatorios"
      class="bg-warning text-white q-mb-lg"
      rounded
    >
      <template #avatar>
        <q-icon name="lock" size="2rem" />
      </template>
      <div class="text-subtitle1 text-weight-bold">Sem permissão no portal Caixa</div>
      <div class="text-body2 q-mt-xs">
        Este CPF não tem acesso ao módulo de Relatórios Parceiros Caixa.<br>
        Solicite à Caixa Econômica a habilitação do operador.
      </div>
    </q-banner>

    <q-banner
      v-else-if="!temPermissaoConexaoCaixaRelatorios"
      class="bg-grey-7 text-white q-mb-lg"
      rounded
    >
      <template #avatar><q-icon name="lock" size="2rem" /></template>
      <div class="text-subtitle1 text-weight-bold">Permissão insuficiente</div>
      <div class="text-body2 q-mt-xs">
        Seu usuário não tem a permissão <strong>ConexaoCaixas.relatorios</strong>.
        Solicite ao administrador da empresa.
      </div>
    </q-banner>

    <div v-else>
      <!-- ── Filtro de data ─────────────────────────────────────── -->
      <div class="row q-col-gutter-md q-mb-md items-end">
        <div class="col-12 col-sm-6">
          <q-input
            v-model="dataFiltro"
            label="Data do movimento"
            mask="##/##/####"
            placeholder="DD/MM/AAAA"
            outlined
            dense
            :disable="processandoLista || processandoDetalhe"
          >
            <template v-slot:append>
              <q-icon name="event" class="cursor-pointer">
                <q-popup-proxy
                  ref="qDateRelatorio"
                  transition-show="scale"
                  transition-hide="scale"
                >
                  <q-date
                    v-model="dataFiltro"
                    @update:model-value="() => $refs.qDateRelatorio.hide()"
                    mask="DD/MM/YYYY"
                    today-btn
                    minimal
                  />
                </q-popup-proxy>
              </q-icon>
            </template>
          </q-input>
        </div>

        <div class="col-12 col-sm-6">
          <q-btn
            color="primary"
            icon="search"
            label="Buscar Resumos de TFL"
            :loading="processandoLista"
            :disable="!podeBuscarLista"
            @click="buscarTerminais"
          />
          <!-- Texto visível, não tooltip: botão desabilitado não dispara hover de
               forma confiável, e o motivo é justamente o que o usuário precisa ler. -->
          <div
            v-if="conexaoCaixaRelatorioBloqueio"
            class="text-caption text-grey-8 q-mt-xs"
          >
            <q-icon name="info" size="xs" class="q-mr-xs" />{{ conexaoCaixaRelatorioBloqueio }}
          </div>
        </div>
      </div>

      <!-- ── Banner: processando lista ─────────────────────────── -->
      <q-banner
        v-if="processandoLista"
        class="bg-info text-white q-mb-md"
        rounded
      >
        <template #avatar>
          <q-spinner color="white" size="2rem" />
        </template>
        {{ conexaoCaixaMensagemProgresso || 'Consultando terminais no portal Parceiros Caixa. Isso pode levar até 1 minuto…' }}
      </q-banner>

      <!-- ── Banner: erro na lista ──────────────────────────────── -->
      <q-banner
        v-if="conexaoCaixaTerminaisTemErro && conexaoCaixaTerminaisErro"
        class="bg-negative text-white q-mb-md"
        rounded
      >
        <template #avatar><q-icon name="error" /></template>
        {{ conexaoCaixaTerminaisErro }}
      </q-banner>

      <!-- ── Detalhe de terminal selecionado ────────────────────── -->
      <div v-if="detalheAtivo">
        <div class="row items-center q-mb-md">
          <q-btn
            flat
            icon="arrow_back"
            label="Voltar à lista"
            color="primary"
            @click="voltarLista"
          />
          <div class="text-h6 q-ml-md">
            {{ terminalDetalheAtivo }}
            <span class="text-subtitle2 text-grey-8">— {{ dataFiltro }}</span>
          </div>
        </div>

        <q-banner
          v-if="processandoDetalhe"
          class="bg-info text-white q-mb-md"
          rounded
        >
          <template #avatar>
            <q-spinner color="white" size="2rem" />
          </template>
          {{ conexaoCaixaMensagemProgresso || 'Gerando relatório detalhado. Isso pode levar até 1 minuto…' }}
        </q-banner>

        <q-banner
          v-if="conexaoCaixaRelatorioTemErro && conexaoCaixaRelatorioErro"
          class="bg-negative text-white q-mb-md"
          rounded
        >
          <template #avatar><q-icon name="error" /></template>
          {{ conexaoCaixaRelatorioErro }}
        </q-banner>

        <div v-if="resultadoDetalhe">
          <q-card flat bordered class="q-mb-md">
            <q-card-section class="row q-col-gutter-md">
              <div class="col-12 col-sm-4">
                <div class="text-caption text-grey-7">Total de créditos</div>
                <div class="text-h6 text-positive">{{ totaisDetalhe.totalCreditos || '—' }}</div>
              </div>
              <div class="col-12 col-sm-4">
                <div class="text-caption text-grey-7">Total de débitos</div>
                <div class="text-h6 text-negative">{{ totaisDetalhe.totalDebitos || '—' }}</div>
              </div>
              <div class="col-12 col-sm-4">
                <div class="text-caption text-grey-7">Saldo final</div>
                <div class="text-h6 text-primary">{{ totaisDetalhe.saldoFinal || '—' }}</div>
              </div>
            </q-card-section>
          </q-card>

          <q-card
            v-for="(secao, idx) in secoesComItens"
            :key="`secao-${idx}`"
            flat
            bordered
            class="q-mb-md"
          >
            <q-expansion-item
              :label="secao.nome"
              :caption="legendaSecao(secao)"
              header-class="bg-grey-1"
              default-opened
            >
              <q-table
                v-if="secao.itens && secao.itens.length > 0"
                :rows="secao.itens"
                :columns="colunasSecao(secao)"
                row-key="__row"
                flat
                dense
                hide-pagination
                v-model:pagination="paginacaoTabela"
                :rows-per-page-options="[0]"
              />
              <div v-else class="q-pa-md text-grey-7 text-italic">Sem itens nesta seção.</div>
            </q-expansion-item>
          </q-card>

          <q-banner
            v-if="secoesInfo.length > 0"
            class="bg-grey-2 q-mb-md"
            rounded
          >
            <div class="text-subtitle2 q-mb-xs">Resumo</div>
            <div
              v-for="(s, i) in secoesInfo"
              :key="`info-${i}`"
              class="text-body2"
            >
              <strong>{{ s.nome }}:</strong> {{ s.total || s.totalQuantidade || '—' }}
            </div>
          </q-banner>
        </div>
      </div>

      <!-- ── Lista de terminais ──────────────────────────────────── -->
      <div v-else-if="conexaoCaixaTerminaisConcluido && listaTerminais.length > 0">
        <div class="text-subtitle1 text-grey-8 q-mb-sm">
          Terminais em {{ dataFiltro }}
        </div>

        <q-card
          v-for="(t, idx) in listaTerminais"
          :key="`terminal-${idx}`"
          flat
          bordered
          class="q-mb-md"
        >
          <q-card-section>
            <div class="row items-center justify-between q-mb-sm">
              <div class="text-subtitle1 text-weight-bold">{{ t.titulo }}</div>
              <q-btn
                outline
                color="primary"
                icon="open_in_new"
                label="Ver detalhes"
                size="sm"
                :loading="processandoDetalhe && terminalDetalheAtivo === t.titulo"
                :disable="processandoDetalhe || !conexaoCaixaPodeRelatorio"
                @click="verDetalhes(t)"
              />
            </div>
            <div class="row q-col-gutter-md">
              <div class="col-4">
                <div class="text-caption text-grey-7">Créditos</div>
                <div class="text-body1 text-positive">{{ t.totalCreditos || '—' }}</div>
              </div>
              <div class="col-4">
                <div class="text-caption text-grey-7">Débitos</div>
                <div class="text-body1 text-negative">{{ t.totalDebitos || '—' }}</div>
              </div>
              <div class="col-4">
                <div class="text-caption text-grey-7">Saldo</div>
                <div class="text-body1 text-primary">{{ t.saldoFinal || '—' }}</div>
              </div>
            </div>
          </q-card-section>
        </q-card>
      </div>

      <!-- ── Estado inicial ─────────────────────────────────────── -->
      <div
        v-else-if="!processandoLista && !conexaoCaixaTerminaisTemErro"
        class="text-center text-grey-5 q-pa-xl"
      >
        <q-icon name="assessment" size="3rem" />
        <div class="text-h6 q-mt-md">Selecione uma data e clique em "Buscar Resumos de TFL"</div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions } from "vuex";

const FORMATO_DATA = /^\d{2}\/\d{2}\/\d{4}$/;

function ddmmyyyyParaIso(s) {
  if (!s || !FORMATO_DATA.test(s)) return null;
  const [d, m, y] = s.split("/");
  return `${y}-${m}-${d}`;
}

function hojeDDMMYYYY() {
  const d = new Date();
  const pad = n => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

export default {
  name: "ConexaoCaixaRelatorios",

  data() {
    return {
      dataFiltro:           hojeDDMMYYYY(),
      terminalDetalheAtivo: null,
      paginacaoTabela:      { rowsPerPage: 0 },
    };
  },

  computed: {
    // Módulo não-namespaced → state fica em state.conexao_caixa.* (não no root).
    ...mapState({
      conexaoCaixaTerminaisErro:   state => state.conexao_caixa.conexaoCaixaTerminaisErro,
      conexaoCaixaTerminaisResult: state => state.conexao_caixa.conexaoCaixaTerminaisResultado,
      conexaoCaixaRelatorioErro:   state => state.conexao_caixa.conexaoCaixaRelatorioErro,
      conexaoCaixaRelatorioResult: state => state.conexao_caixa.conexaoCaixaRelatorioResultado,
    }),
    ...mapGetters([
      "conexaoCaixaSemAcessoRelatorios",
      "conexaoCaixaVinculoAtivo",
      "conexaoCaixaTerminaisProcessando",
      "conexaoCaixaTerminaisConcluido",
      "conexaoCaixaTerminaisTemErro",
      "conexaoCaixaRelatorioProcessando",
      "conexaoCaixaRelatorioConcluido",
      "conexaoCaixaRelatorioTemErro",
      "temPermissaoConexaoCaixaRelatorios",
      "conexaoCaixaMensagemProgresso",
      "conexaoCaixaPodeRelatorio",
      "conexaoCaixaRelatorioBloqueio",
    ]),

    processandoLista()    { return this.conexaoCaixaTerminaisProcessando; },
    processandoDetalhe()  { return this.conexaoCaixaRelatorioProcessando; },
    detalheAtivo()        { return this.terminalDetalheAtivo !== null; },

    listaTerminais() {
      return (this.conexaoCaixaTerminaisResult && this.conexaoCaixaTerminaisResult.terminais) || [];
    },

    resultadoDetalhe() {
      return this.conexaoCaixaRelatorioResult;
    },

    podeBuscarLista() {
      return (
        !this.processandoLista &&
        !this.processandoDetalhe &&
        // Cobre vínculo ativo + agente online + logado no portal. Sem os três o
        // backend devolve 400, e o erro chegava na tela depois do clique.
        this.conexaoCaixaPodeRelatorio &&
        FORMATO_DATA.test(this.dataFiltro || "")
      );
    },

    totaisDetalhe() {
      return (this.resultadoDetalhe && this.resultadoDetalhe.totaisFinais) || {};
    },

    secoesComItens() {
      const secoes = (this.resultadoDetalhe && this.resultadoDetalhe.secoes) || [];
      return secoes.filter(s => Array.isArray(s.itens));
    },

    secoesInfo() {
      const secoes = (this.resultadoDetalhe && this.resultadoDetalhe.secoes) || [];
      return secoes.filter(s => !Array.isArray(s.itens));
    },
  },

  watch: {
    conexaoCaixaRelatorioConcluido(novo) {
      if (novo) {
        this.$q.notify({
          message:  "Relatório carregado com sucesso.",
          color:    "green",
          position: "top-right",
        });
      }
    },
    conexaoCaixaRelatorioTemErro(novo) {
      if (novo && this.conexaoCaixaRelatorioErro) {
        this.notificarErroConexaoCaixa(this.conexaoCaixaRelatorioErro);
      }
    },
    conexaoCaixaTerminaisTemErro(novo) {
      if (novo && this.conexaoCaixaTerminaisErro) {
        this.notificarErroConexaoCaixa(this.conexaoCaixaTerminaisErro);
      }
    },
  },

  beforeUnmount() {
    this.pararPollingRelatorio();
    this.pararPollingTerminais();
  },

  methods: {
    ...mapActions([
      "iniciarListaTerminais",
      "iniciarRelatorioTerminalCaixa",
      "pararPollingRelatorio",
      "pararPollingTerminais",
    ]),

    notificarErroConexaoCaixa(mensagem) {
      if (!mensagem) return;

      if (typeof mensagem === "string" && mensagem.startsWith("Sessão da Caixa expirou")) {
        this.$q.notify({
          message:  mensagem,
          color:    "orange",
          position: "top-right",
          timeout:  0,
          actions: [
            {
              label:   "Reconectar",
              color:   "white",
              handler: () => { this.$router.push("/parametros"); },
            },
            { label: "Fechar", color: "white" },
          ],
        });
        return;
      }

      this.$q.notify({
        message:  mensagem,
        color:    "red",
        position: "top-right",
      });
    },

    async buscarTerminais() {
      if (!this.podeBuscarLista) return;
      this.terminalDetalheAtivo = null;
      this.pararPollingRelatorio();
      const dataIso = ddmmyyyyParaIso(this.dataFiltro);
      await this.iniciarListaTerminais({ data: dataIso });
    },

    async verDetalhes(terminal) {
      const match = (terminal.titulo || "").match(/\d+/);
      if (!match) return;
      this.terminalDetalheAtivo = terminal.titulo;
      const dataIso = ddmmyyyyParaIso(this.dataFiltro);
      await this.iniciarRelatorioTerminalCaixa({
        terminal: match[0],
        data:     dataIso,
      });
    },

    voltarLista() {
      this.terminalDetalheAtivo = null;
      this.pararPollingRelatorio();
    },

    legendaSecao(secao) {
      if (!secao) return "";
      if (secao.total) return secao.total;
      if (secao.totalQuantidade) return `Qtd: ${secao.totalQuantidade}`;
      return "";
    },

    colunasSecao(secao) {
      const colunas = (secao && secao.colunas) || ["Descrição", "Valor"];
      return colunas.map(nome => ({
        name:     nome,
        label:    nome,
        field:    row => row[nome],
        align:    nome.toLowerCase().includes("valor") ? "right" : "left",
        sortable: false,
      }));
    },
  },
};
</script>
